import { useCallback, useEffect, useState } from 'react';
import type { Team } from './teams';
import type { Ticket } from './mock';
import { isOpen } from './mock';
import { supabase } from './supabase';
import { configurationsApi } from '../api/configurationsApi';

/**
 * Auto-routing of new tickets to a team member. Strategies: round-robin,
 * load-based (fewest open), or skills (category → member, then load).
 * Persisted in Supabase `configurations` table (`key: 'routing_policies'`).
 */
export type RoutingStrategy = 'manual' | 'round-robin' | 'load-based' | 'skills';
export type RoutingPolicy = {
  strategy: RoutingStrategy;
  skills?: Record<string, string[]>; // member → categories they handle
};

export const DEFAULT_ROUTING: RoutingPolicy = { strategy: 'manual', skills: {} };

let rrIndexMap: Record<string, number> = {};

function rrPick(teamId: string, members: string[]): string {
  const next = ((rrIndexMap[teamId] ?? -1) + 1) % members.length;
  rrIndexMap[teamId] = next;
  return members[next];
}

/** Choose an assignee for a new ticket, or null for manual. */
export function pickAssignee(
  policy: RoutingPolicy,
  team: Team,
  category: string,
  allTickets: Ticket[],
): string | null {
  const members = team.members;
  if (!members.length || policy.strategy === 'manual') return null;

  const open = allTickets.filter((t) => isOpen(t));
  const loadOf = (m: string) => open.filter((t) => t.assignee === m).length;
  const leastLoaded = (pool: string[]) =>
    pool.reduce((best, m) => (loadOf(m) < loadOf(best) ? m : best), pool[0]);

  if (policy.strategy === 'round-robin') return rrPick(team.id, members);
  if (policy.strategy === 'load-based') return leastLoaded(members);
  if (policy.strategy === 'skills') {
    const skilled = members.filter((m) =>
      (policy.skills?.[m] ?? []).some((c) => c.toLowerCase() === category.toLowerCase()),
    );
    return leastLoaded(skilled.length ? skilled : members);
  }
  return null;
}

export function useRoutingPolicy(teamId: string) {
  const [map, setMap] = useState<Record<string, RoutingPolicy>>({});
  const policy = map[teamId] ?? DEFAULT_ROUTING;

  useEffect(() => {
    const loadRouting = async () => {
      try {
        const data = await configurationsApi.getConfiguration('routing_policies');
        if (data && data.value) {
          setMap(data.value);
          return;
        }
      } catch (e) {
        // Fallback
      }

      if (supabase) {
        try {
          const { data } = await supabase
            .from('configurations')
            .select('*')
            .eq('key', 'routing_policies')
            .maybeSingle();

          if (data && data.value) {
            setMap(data.value);
          }
        } catch (err) {
          console.error('Failed to load routing policies:', err);
        }
      }
    };
    loadRouting();
  }, []);

  const save = useCallback(
    async (next: RoutingPolicy) => {
      const merged = { ...map, [teamId]: next };
      setMap(merged);

      try {
        await configurationsApi.saveConfiguration('routing_policies', merged);
      } catch (err) {
        console.error('Failed to save routing policy via API:', err);
      }
    },
    [map, teamId],
  );

  return { policy, save };
}
